import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import IntegrationsMparticleModel from 'cio/models/integrations/mparticle';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import { assertExists } from 'cio/utils/assertions';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';

export default class IntegrationsMparticleController extends Controller {
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service router!: RouterService;

  @tracked integration?: IntegrationsMparticleModel;

  resetDefaults() {
    this.integration = undefined;
  }

  get isConnected() {
    return !!this.integration;
  }

  get canSaveIntegration() {
    return this.integration?.hasDirtyAttributes && this.integration.events?.length > 0;
  }

  fetchIntegrationTask = task(async () => {
    try {
      this.integration = await this.store.queryRecord('integrations/mparticle', {});
    } catch (e) {
      // When there is no integration, it will return a 404. That is an acceptable error,
      // but we should throw on all other error types.
      if (e.errors[0].status !== '404') {
        throw e;
      }
    }
  });

  connectIntegrationTask = task(async (key: string, secret: string) => {
    let integration = this.store.createRecord('integrations/mparticle', { key, secret });

    try {
      await integration.save();
      this.integration = integration;
    } catch (e) {
      this.apiErrors.report(e);
      integration.rollbackAttributes();
    }
  });

  disconnectIntegrationTask = task(async () => {
    assertExists(this.integration);

    try {
      await this.integration.destroyRecord();
      this.alerts.success('mparticle-delete.success', 'mParticle integration removed');
      this.integration = undefined;
    } catch {
      this.integration?.rollbackAttributes();
      this.alerts.error(
        'mparticle-delete.error',
        'Could not remove mParticle integration. Please try again.',
        'Whoops!',
      );
    }
  });

  saveIntegrationTask = task(async () => {
    assertExists(this.integration);

    try {
      await this.integration.save();
      this.alerts.success('mparticle.success', 'mParticle integration settings saved');
    } catch (err) {
      this.integration.rollbackAttributes();
      this.alerts.error('mparticle-save.error', 'Could not update mParticle integration. Please try again.', 'Whoops!');
    }
  });

  @action
  cancel() {
    this.integration?.rollbackAttributes();
    this.router.transitionTo('integrations');
  }
}
